// Wave 20.20 — Public /privacy page. Linked from the footer on the
// marketing homepage and every auth screen. Fetches the current
// version of the seeded privacy policy from GET /public/legal/privacy
// (no auth required) and renders the body in a readable card.

import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import PageLayout from './components/PageLayout';
import { colors } from './theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';

interface LegalDoc {
  kind: 'privacy';
  version: string;
  effectiveAt: string;
  body: string;
}

export default function Privacy() {
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API}/public/legal/privacy`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        setDoc(await r.json());
      })
      .catch((e) => setError(e?.message || 'Failed to load'));
  }, []);

  return (
    <PageLayout maxWidth="md">
      <Container maxWidth="md" sx={{ py: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
              AeroChat Inc.
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 900 }}>
              Privacy Policy
            </Typography>
            {doc && (
              <Typography sx={{ color: colors.textMuted, mt: 0.5, fontSize: '0.85rem' }}>
                Effective {new Date(doc.effectiveAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>

          {error && (
            <Typography sx={{ color: colors.danger, fontSize: '0.9rem' }}>
              Couldn't load the privacy policy: {error}
            </Typography>
          )}

          {!doc && !error && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: colors.brand }} />
            </Box>
          )}

          {doc && (
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.7,
                color: colors.text,
                fontSize: '0.95rem',
              }}
            >
              {doc.body}
            </Box>
          )}
        </Stack>
      </Container>
    </PageLayout>
  );
}
